import {
  ApprovalStatus,
  Barcode,
  GlobalProduct,
} from '@bofrak-backend/shared';

export const GLOBAL_PRODUCT_CATALOG_ID = 'global-products';

export type GlobalProductSyncState = 'idle' | 'syncing' | 'error';

export interface CachedGlobalProduct extends GlobalProduct {
  _cache_revision: number;
  _cached_at: string;
  _local_write_at?: number;
}

export interface GlobalProductBarcodeCacheEntry {
  barcode_value: string;
  product_id: string;
  barcode_type: string;
  description?: string;
}

export interface GlobalProductSyncMetadata {
  id: typeof GLOBAL_PRODUCT_CATALOG_ID;
  status: GlobalProductSyncState;
  revision: number;
  total_products: number;
  last_started_at?: string;
  last_successful_sync?: string;
  error?: string;
}

export type GlobalProductImportSource =
  | 'csv'
  | 'merchant-bulk'
  | 'merchant-wizard'
  | 'merchant-compare';

export type GlobalProductImportStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'skipped'
  | 'failed';

export interface GlobalProductImportJob {
  id: string;
  source: GlobalProductImportSource;
  status: GlobalProductImportStatus;
  source_store_id?: string;
  created_at: string;
  updated_at: string;
  total_families: number;
  completed_families: number;
  warning_count: number;
  failure_count: number;
}

export interface GlobalProductImportCandidate {
  job_id: string;
  source_product_id: string;
  family_id: string;
  status: GlobalProductImportStatus;
  is_parent: boolean;
  parent_source_id: string;
  name: string;
  description: string;
  image?: string;
  quantity_in_unit_package: number;
  weight_kg: number;
  unit: string | number;
  unit_of_measurement: string;
  /** ISO country code (e.g. "MZ") the created/updated global product belongs to. */
  country?: string;
  barcodes: Barcode[];
  suggested_cost?: number | null;
  resulting_global_product_id?: string;
  skipped_barcodes?: string[];
  /** How the pre-import review resolved this member against the catalog. */
  match_kind?: 'create' | 'overwrite' | 'conflict';
  matched_global_product_id?: string;
  decision?: 'import' | 'ignore' | 'create-new';
  warning?: string;
  error?: string;
  created_at: string;
  updated_at: string;
}

export interface GlobalProductCatalogSyncRequest {
  type: 'SYNC';
  requestId: string;
  apiEndpoint: string;
  accessToken?: string;
}

export type GlobalProductCatalogSyncResponse =
  | {
      type: 'SYNC_PROGRESS';
      requestId: string;
      loaded: number;
      page: number;
    }
  | {
      type: 'SYNC_COMPLETE';
      requestId: string;
      loaded: number;
      revision: number;
    }
  | {
      type: 'SYNC_ERROR';
      requestId: string;
      error: string;
    };

export interface GlobalProductSearchRequest {
  type: 'SEARCH';
  requestId: string;
  query: string;
  approvalStatus?: ApprovalStatus;
  parentsOnly?: boolean;
}

export type GlobalProductSearchResponse =
  | {
      type: 'SEARCH_COMPLETE';
      requestId: string;
      revision: number;
      productIds: string[];
    }
  | {
      type: 'SEARCH_ERROR';
      requestId: string;
      error: string;
    };